import { sortBy } from 'lodash/fp';
import { FC, useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom-v5-compat';

import LoadingModal from 'dashboard/components/LoadingModal';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import WorkflowEditor from 'dashboard/components/WorkflowEditor';
import { CallerUIState } from 'dashboard/components/WorkflowModuleCallersList/WorkflowModuleCallersList';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { TemplateFilter } from 'dashboard/lib/workflows/objects';
import { Environment } from 'dashboard/models/environment';
import { Organization } from 'dashboard/models/organization';
import { WorkflowError, WorkflowSchema } from 'dashboard/models/workflow';
import { WorkflowBlockStatus } from 'dashboard/models/workflow-block';
import {
  WorkflowModule,
  WorkflowModulePropagationResult,
  WorkflowModuleStatus,
} from 'dashboard/models/workflow-module';
import { WorkflowStep, WorkflowStepInput } from 'dashboard/models/workflow-version';
import { showMessage } from 'dashboard/redux/actions/messages';
import {
  useCreateWorkflowBlockMutation,
  useDiscardWorkflowBlockMutation,
  useLazyGetWorkflowBlockQuery,
  useLazyListWorkflowBlocksQuery,
} from 'dashboard/redux/api/workflow-block';
import { usePropagateWorkflowModuleMutation } from 'dashboard/redux/api/workflow-module';
import { ObjectSchemaMap } from 'dashboard/redux/slices/object-schemas';
import { BlockData } from 'dashboard/redux/slices/workflow-block';
import { BlockUpdateData } from 'dashboard/redux/thunk-actions/workflow-block';
import { ConfigQueryParam } from 'dashboard/routes/DashboardWorkflowsShowRoute/types';
import PropagationModalContent from 'dashboard/views/DashboardWorkflowModulesShowView/PropagationModalContent';
import WorkflowModulePublishModal from 'dashboard/views/DashboardWorkflowModulesShowView/WorkflowModulePublishModal';
import WorkflowConfigModal, {
  EditorNavItemLinkProps,
  ModalSectionView,
} from 'dashboard/views/DashboardWorkflowsShowView/WorkflowConfigModal';
import WorkflowHeader, {
  MoreMenuItemKey,
} from 'dashboard/views/DashboardWorkflowsShowView/WorkflowHeader';
import WorkflowStatusBanner from 'dashboard/views/DashboardWorkflowsShowView/WorkflowStatusBanner';
import WorkflowVersionDiffModal from 'dashboard/views/DashboardWorkflowsShowView/WorkflowVersionDiffModal';
import WorkflowModuleModals from 'dashboard/views/DashboardWorkflowsView/WorkflowModuleModals';
import useBatchMutation from 'lib/hooks/useBatchMutation';
import useBatchQuery from 'lib/hooks/useBatchQuery';
import useConfirmation from 'lib/hooks/useConfirmation';
import useIsPersonerd from 'lib/hooks/useIsPersonerd';
import useOnMountAndUnmount from 'lib/hooks/useOnMountAndUnmount';
import usePermission from 'lib/hooks/usePermission';
import { usePreserveQueries } from 'lib/hooks/usePreserveQuery';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

interface DashboardWorkflowModulesShowViewProps {
  blockData: BlockData;
  blockId: string;
  blockStatus: WorkflowBlockStatus;
  canUndo: boolean;
  configQueryParam: ConfigQueryParam;
  currentEnvironmentType: Environment['type'];
  editableBlockId: string;
  inputStep: WorkflowStepInput;
  isDirty: boolean;
  loading: boolean;
  organization: Organization;
  stepsConfig: WorkflowStep[];
  validationErrors: WorkflowError[];
  workflowModule: WorkflowModule;
  workflowSchema: WorkflowSchema;
  clearWorkflowHistory: () => void;
  handleClickSave: () => void;
  handleClickPublish: (workflowIds: string[]) => Promise<WorkflowModulePropagationResult[]>;
  loadPreviousBlock: (id: string) => void;
  setInputStep: (inputStep: WorkflowStepInput) => void;
  showMessage: (...args: Parameters<typeof showMessage>) => void;
  undoWorkflowModule: () => void;
  updateWorkflowModule: (updatedData: WorkflowModule) => Promise<void>;
  updateVersionConfig: (data: Partial<BlockUpdateData>) => void;
  fetchPlatformObjectJsonSchema: (template?: TemplateFilter) => Promise<ObjectSchemaMap>;
}

const DashboardWorkflowModulesShowView: FC<DashboardWorkflowModulesShowViewProps> = ({
  blockData,
  blockId,
  blockStatus,
  canUndo,
  configQueryParam,
  currentEnvironmentType,
  editableBlockId,
  inputStep,
  isDirty,
  loading,
  organization,
  stepsConfig,
  validationErrors,
  workflowModule,
  workflowSchema,
  clearWorkflowHistory,
  handleClickSave,
  handleClickPublish,
  loadPreviousBlock,
  setInputStep,
  showMessage,
  undoWorkflowModule,
  updateWorkflowModule,
  updateVersionConfig,
  fetchPlatformObjectJsonSchema,
}) => {
  const navigate = useNavigate();
  const hasPermission = usePermission(Permission.WorkflowWrite);
  const isPersonerd = useIsPersonerd();
  const preserveQueries = usePreserveQueries();

  const {
    id: workflowModuleId,
    attributes: {
      name: workflowModuleName,
      status: workflowModuleStatus,
      description: workflowModuleDescription,
    },
    relationships: {
      'latest-published-block': latestPublishedBlock,
      'latest-draft-block': latestDraftBlock,
    },
  } = workflowModule;
  const hasLatestDraft = latestDraftBlock.data?.id != null;
  const isLatestDraft = blockId === latestDraftBlock.data?.id;
  const hasLatestPublished = latestPublishedBlock.data?.id != null;
  const isLatestPublished = blockId === latestPublishedBlock.data?.id;
  const isEditableVersion =
    (hasLatestDraft && isLatestDraft) || (!hasLatestDraft && isLatestPublished);
  const isBlockArchived = blockStatus === WorkflowBlockStatus.Archived;
  const isShowingHeaderStatus = isBlockArchived || !isEditableVersion;
  const latestEditableVersionId = hasLatestDraft
    ? latestDraftBlock.data?.id
    : latestPublishedBlock.data?.id;
  const latestEditableVersionUrl = `/dashboard/workflows/modules/${workflowModuleId}?${preserveQueries(
    { version: latestEditableVersionId || '' },
  )}`;
  const callers = useMemo(
    () => sortBy('source-id', workflowModule.attributes.callers),
    [workflowModule.attributes.callers],
  );

  const [visibleValidationErrors, { enable: showValidationErrors, disable: hideValidationErrors }] =
    useToggle(false);
  const [showPublishModal, { enable: openPublishModal, disable: closePublishModal }] =
    useToggle(false);
  const [showVersionDiffModal, { enable: openVersionDiffModal, disable: closeVersionDiffModal }] =
    useToggle(false);
  const [
    showDiscardDraftModal,
    { prompt: discardPrompt, confirm: confirmDiscardDraft, cancel: cancelDiscardDraft },
  ] = useConfirmation();

  const [focusedValidationErrorIndex, setFocusedValidationErrorIndex] = useState<number | null>(
    null,
  );
  const [moduleToActivate, setModuleToActivate] = useState<WorkflowModule | null>(null);
  const [moduleToArchive, setModuleToArchive] = useState<WorkflowModule | null>(null);
  const [moduleToDeactivate, setModuleToDeactivate] = useState<WorkflowModule | null>(null);
  const [propagationResults, setPropagationResults] = useState<WorkflowModulePropagationResult[]>(
    [],
  );
  const [callerUIStates, setCallerUIStates] = useState<CallerUIState[]>(
    sortBy(
      'id',
      callers.map(({ 'source-id': workflowId }) => ({
        id: workflowId,
        selected: false,
      })),
    ),
  );
  const [isShowingVersions, { enable: showVersions, disable: hideVersions }] = useToggle(false);
  const [getLatestPublishedBlock, latestPublishedBlockQuery] = useLazyGetWorkflowBlockQuery();
  const [getBlockList, blockListLazyQuery] = useLazyListWorkflowBlocksQuery();
  const [restoreDraft, restoreDraftMutation] = useCreateWorkflowBlockMutation();
  const [propagate, propagateMutation] = usePropagateWorkflowModuleMutation();
  const [discardDraft, discardDraftMutation] = useDiscardWorkflowBlockMutation();
  const editable = isEditableVersion && !isShowingVersions;

  useBatchQuery({
    queries: [latestPublishedBlockQuery, blockListLazyQuery],
    onError: ({ requestId }) => {
      switch (requestId) {
        case latestPublishedBlockQuery.requestId:
          return { message: 'Failed to load the latest published version.' };
        case blockListLazyQuery.requestId:
          return { message: 'Failed to load previous versions.' };
        case restoreDraftMutation.requestId:
          return { message: 'Failed to restore previous version.' };
        case discardDraftMutation.requestId:
          return { message: 'Failed to discard draft version.' };
      }
    },
  });

  useBatchMutation({
    mutations: [restoreDraftMutation, propagateMutation],
    onError: ({ requestId }) => {
      switch (requestId) {
        case restoreDraftMutation.requestId:
          return { message: 'Failed to restore previous version.' };
        case propagateMutation.requestId:
          return { message: 'Failed to update workflows.' };
      }
    },
  });

  const { data: latestPublishedBlockResponse } = latestPublishedBlockQuery;
  const { data: blockListResponse } = blockListLazyQuery;

  useOnMountAndUnmount(() => {
    clearWorkflowHistory();
  });

  const loadBlockList = useCallback(async () => {
    await getBlockList(
      {
        filter: { parent_type: ApiModel.WorkflowModule, parent_ids: [workflowModuleId] },
        page: {
          number: 1,
          size: 100,
        },
      },
      true,
    ).unwrap();
  }, [getBlockList, workflowModuleId]);

  const restoreDraftVersion = useCallback(async () => {
    await restoreDraft({
      parentType: ApiModel.WorkflowModule,
      parentId: workflowModuleId,
      sourceBlockId: blockId,
    }).unwrap();
    showMessage('Successfully restored draft version.', MessageType.Success);
  }, [restoreDraft, workflowModuleId, blockId, showMessage]);

  const setDescription = useCallback(
    (description: string) => {
      updateVersionConfig({
        description,
      });
    },
    [updateVersionConfig],
  );

  const closeVersionsView = useCallback(() => {
    if (blockId !== editableBlockId) {
      loadPreviousBlock(editableBlockId);
    }
    hideVersions();
  }, [blockId, editableBlockId, loadPreviousBlock, hideVersions]);

  const handleClickWorkflowValidationError = useCallback(
    (errorIndex: number) => {
      setFocusedValidationErrorIndex(errorIndex);
      showValidationErrors();
    },
    [setFocusedValidationErrorIndex, showValidationErrors],
  );

  const proceedToPublishModal = useCallback(() => {
    openPublishModal();
    hideValidationErrors();
  }, [hideValidationErrors, openPublishModal]);

  const handleReviewWorkflowChanges = useCallback(() => {
    if (validationErrors.length > 0) {
      showMessage('Please fix errors before publishing', MessageType.Notice);
      showValidationErrors();
      return;
    }
    if (!isLatestPublished && latestPublishedBlock.data?.id != null && isPersonerd) {
      getLatestPublishedBlock(latestPublishedBlock.data.id, true);
      openVersionDiffModal();
    } else {
      proceedToPublishModal();
    }
  }, [
    validationErrors,
    showMessage,
    showValidationErrors,
    isLatestPublished,
    latestPublishedBlock.data?.id,
    isPersonerd,
    getLatestPublishedBlock,
    openVersionDiffModal,
    proceedToPublishModal,
  ]);

  const confirmPublish = useCallback(
    async (selectedWorkflows) => {
      const results = await handleClickPublish(selectedWorkflows);
      if (!selectedWorkflows.length) {
        closePublishModal();
      }
      return results;
    },
    [handleClickPublish, closePublishModal],
  );

  const handleDiscardDraft = useCallback(async () => {
    try {
      await discardDraft({
        parentType: ApiModel.WorkflowModule,
        parentId: workflowModuleId,
        id: blockId,
      }).unwrap();

      showMessage('Successfully discarded draft version.', MessageType.Success);
      if (latestPublishedBlock.data?.id) {
        loadPreviousBlock(latestPublishedBlock.data.id);
      }
    } catch {
      showMessage('Failed to discard draft version.', MessageType.Failure);
    }
  }, [
    blockId,
    discardDraft,
    latestPublishedBlock.data?.id,
    loadPreviousBlock,
    showMessage,
    workflowModuleId,
  ]);

  const onClickDiscard = useCallback(
    () => discardPrompt(() => handleDiscardDraft()),
    [discardPrompt, handleDiscardDraft],
  );

  const submitPropagation = useCallback(async () => {
    const selectedWorkflowIds = callers
      .filter((_, index) => callerUIStates[index].selected)
      .map(({ 'source-id': workflowId }) => workflowId);
    const { meta } = await propagate({
      workflowModuleId,
      workflowIds: selectedWorkflowIds,
    }).unwrap();
    if (meta) {
      setPropagationResults(meta.results);
      setCallerUIStates(
        callerUIStates.map(({ id }) => {
          return {
            id,
            selected: false,
          };
        }),
      );
    }
  }, [workflowModuleId, callerUIStates, callers, propagate]);

  const closeConfigModal = useCallback(() => {
    setCallerUIStates(
      callerUIStates.map((config) => ({
        ...config,
        result: undefined,
      })),
    );
    setPropagationResults([]);
    navigate(`/dashboard/workflows/modules/${workflowModuleId}?version=${blockId}`, {
      replace: true,
    });
  }, [navigate, workflowModuleId, blockId, callerUIStates]);

  const onUpdateCallerUIState = useCallback((callerConfig, index) => {
    setCallerUIStates((currentConfigs) =>
      Object.assign([], currentConfigs, {
        [index]: callerConfig,
      }),
    );
  }, []);

  const usageNavItem: EditorNavItemLinkProps = useMemo(
    () => ({
      id: 'usage',
      label: 'Usage',
      route: {
        pathname: '.',
        search: preserveQueries({ version: blockId, config: 'usage' }),
      },
    }),
    [preserveQueries, blockId],
  );

  const configSections: ModalSectionView[] = useMemo(() => {
    const showPropagationResults = !!propagationResults.length;
    const callersSelected = callerUIStates.some(({ selected }) => selected);
    return [
      {
        title: usageNavItem.label,
        path: 'usage',
        configParam: 'usage',
        contents: (
          <PropagationModalContent
            callers={callers}
            callerUIStates={callerUIStates}
            latestPublishedBlockId={latestPublishedBlock.data?.id || ''}
            propagationResults={propagationResults}
            onUpdateCallerUIState={onUpdateCallerUIState}
          />
        ),
        navItem: usageNavItem,
        onSubmit: showPropagationResults ? closeConfigModal : submitPropagation,
        isSubmitting: false,
        disableSubmit:
          workflowModuleStatus === WorkflowModuleStatus.Inactive ||
          (!showPropagationResults && !callersSelected),
        submitButtonText: showPropagationResults ? 'Done' : 'Update',
      },
    ];
  }, [
    callers,
    usageNavItem,
    workflowModuleStatus,
    callerUIStates,
    onUpdateCallerUIState,
    closeConfigModal,
    submitPropagation,
    latestPublishedBlock.data?.id,
    propagationResults,
  ]);

  const diffConfigs = useMemo(() => {
    if (!latestPublishedBlockResponse) {
      return [];
    }

    return [
      {
        comparedObject: {
          inputs: latestPublishedBlockResponse.data.attributes.inputs,
        },
        currentObject: { inputs: inputStep.inputs },
        description: 'Inputs',
      },
      {
        comparedObject: {
          'steps-config': latestPublishedBlockResponse.data.attributes['steps-config'],
        },
        currentObject: { 'steps-config': stepsConfig },
        description: 'Step configurations',
      },
      {
        comparedObject: {
          outputs: latestPublishedBlockResponse.data.attributes.outputs,
        },
        currentObject: { outputs: blockData.outputs },
        description: 'Outputs',
      },
    ];
  }, [latestPublishedBlockResponse, stepsConfig, inputStep, blockData]);

  return (
    <>
      <Helmet title={`${workflowModuleName} - Workflow Modules`} />
      <WorkflowModuleModals
        moduleToActivate={moduleToActivate}
        moduleToArchive={moduleToArchive}
        moduleToDeactivate={moduleToDeactivate}
        setModuleToActivate={setModuleToActivate}
        setModuleToArchive={setModuleToArchive}
        setModuleToDeactivate={setModuleToDeactivate}
      />
      <ModalWrapper
        contentLabel='Publish workflow module'
        isOpen={showPublishModal || showVersionDiffModal}
        handleCloseModal={closeVersionDiffModal}
      >
        {showVersionDiffModal && (
          <WorkflowVersionDiffModal
            baseVersionId={latestPublishedBlockResponse?.data.id || ''}
            diffConfigs={diffConfigs}
            headerText='Workflow Module'
            targetVersionId={blockId}
            loading={latestPublishedBlockQuery.isLoading}
            handleClose={closeVersionDiffModal}
            handlePublish={proceedToPublishModal}
          />
        )}
        {showPublishModal && (
          <WorkflowModulePublishModal
            description={blockData.description}
            workflowModule={workflowModule}
            cancelAction={closePublishModal}
            confirmAction={confirmPublish}
            setDescription={setDescription}
          />
        )}
      </ModalWrapper>
      <ModalWrapper
        contentLabel='Workflow module configuration'
        isOpen={!!configQueryParam}
        handleCloseModal={closeConfigModal}
      >
        <WorkflowConfigModal
          configQueryParam={configQueryParam}
          description={workflowModuleDescription}
          name={workflowModuleName}
          sectionViews={configSections}
          versionId={blockId}
          handleCloseModal={closeConfigModal}
          onUpdateSettings={({ name, description }) =>
            updateWorkflowModule({
              ...workflowModule,
              attributes: {
                ...workflowModule.attributes,
                name: name || workflowModuleName,
                description: description || workflowModuleDescription,
              },
            })
          }
        />
      </ModalWrapper>
      <ModalWrapper isOpen={showDiscardDraftModal} handleCloseModal={cancelDiscardDraft}>
        <ConfirmationModal
          cancelAction={cancelDiscardDraft}
          confirmAction={confirmDiscardDraft}
          header='Discard draft?'
          confirmActionText='Discard'
        >
          All unpublished changes in this draft will be discarded. This action is permanent and
          cannot be undone.
        </ConfirmationModal>
      </ModalWrapper>
      {isShowingHeaderStatus && (
        <WorkflowStatusBanner
          isVersionArchived={isBlockArchived}
          isInRollout={false}
          latestEditableVersionUrl={latestEditableVersionUrl}
          isShowingWorkflowVersions={false}
          showWorkflowVersions={showVersions}
          versionStatus={blockStatus}
          workflowName={workflowModuleName}
        />
      )}
      <WorkflowHeader
        basePathRoot='workflows/modules'
        canUndo={canUndo}
        currentEnvironmentType={currentEnvironmentType}
        editable={editable}
        hasStepsConfig={!!stepsConfig.length}
        hasWritePermission={hasPermission}
        inputStep={inputStep}
        isCurrentPublishedVersion={isLatestPublished}
        hasLatestPublishedVersion={hasLatestPublished}
        isDirty={isDirty}
        isParentActive={workflowModuleStatus === WorkflowModuleStatus.Active}
        isParentActiveInEnvironment={workflowModuleStatus === WorkflowModuleStatus.Active}
        isParentArchived={workflowModuleStatus === WorkflowModuleStatus.Archived}
        isParentInactive={workflowModuleStatus === WorkflowModuleStatus.Inactive}
        isShowingVersionHistory={isShowingVersions}
        isVersionActivatable={stepsConfig.length !== 0 && hasLatestPublished}
        loading={loading}
        moreMenuItemKeys={[
          MoreMenuItemKey.Settings,
          MoreMenuItemKey.VersionHistory,
          MoreMenuItemKey.Deactivate,
          MoreMenuItemKey.Activate,
          MoreMenuItemKey.Archive,
        ]}
        organization={organization}
        parentId={workflowModuleId}
        parentName={workflowModuleName}
        validationErrors={validationErrors}
        versionId={blockId}
        versionStatus={blockStatus}
        closeWorkflowVersionView={closeVersionsView}
        onClickError={handleClickWorkflowValidationError}
        onClickSave={handleClickSave}
        onClickDiscard={onClickDiscard}
        onUpdateWorkflow={(attr) => {
          updateWorkflowModule({
            ...workflowModule,
            attributes: {
              ...workflowModule.attributes,
              name: attr.name ?? workflowModule.attributes.name,
              description: attr.description ?? workflowModule.attributes.description,
            },
          });
        }}
        openActivateWorkflowModal={() => setModuleToActivate(workflowModule)}
        openArchiveWorkflowModal={() => setModuleToArchive(workflowModule)}
        openDeactivateWorkflowModal={() => setModuleToDeactivate(workflowModule)}
        openManualTriggerModal={() => {}}
        openPublishWorkflowModal={handleReviewWorkflowChanges}
        showWorkflowVersions={showVersions}
        createSimulatedWorkflowRun={() => {}}
        undoWorkflow={undoWorkflowModule}
      />
      <LoadingModal isLoading={loading} />
      <WorkflowEditor
        closeWorkflowVersionView={closeVersionsView}
        editable={editable && hasPermission}
        focusedValidationErrorIndex={focusedValidationErrorIndex ?? undefined}
        inputStep={inputStep}
        setInputStep={setInputStep}
        isDirty={isDirty}
        isLoadingVersionList={blockListLazyQuery.isLoading}
        isShowingHeaderStatus={isShowingHeaderStatus}
        isShowingWorkflowVersions={isShowingVersions}
        latestDraftVersionId={workflowModule.relationships['latest-draft-block'].data?.id || ''}
        loadVersionList={loadBlockList}
        loadWorkflowVersion={loadPreviousBlock}
        parentId={workflowModule.id}
        parentName={workflowModule.attributes.name}
        restoreDraftVersion={restoreDraftVersion}
        updateVersionConfig={updateVersionConfig}
        handleDiscardDraft={handleDiscardDraft}
        validationErrors={visibleValidationErrors ? validationErrors : []}
        versionConfig={{
          ...blockData,
          'steps-config': stepsConfig,
        }}
        versionId={blockId}
        versionsList={[...(blockListResponse?.data || [])]}
        workflowSchema={workflowSchema}
        actionMetaLocation='modules'
        clearFocusedValidationErrorIndex={() => setFocusedValidationErrorIndex(null)}
        fetchPlatformObjectJsonSchema={fetchPlatformObjectJsonSchema}
      />
    </>
  );
};

export default DashboardWorkflowModulesShowView;
